"use client";

import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";

type HireBuilderPreviewPanelProps = {
  previewProjectId: string | null;
  previewReady: boolean;
  /** False until the user submits their website URL in Build onboarding. */
  websiteSubmitted: boolean;
  /** False until onboarding goals are complete (preview generation starts). */
  previewGenerationReady: boolean;
  agentName: string;
  isGenerating: boolean;
  previewRefreshKey: number;
  className?: string;
  /** SiteGlue copy uses AI Employee / SiteGlue branding. */
  siteGlue?: boolean;
};

export function HireBuilderPreviewPanel({
  previewProjectId,
  previewReady,
  websiteSubmitted,
  previewGenerationReady,
  agentName,
  isGenerating,
  previewRefreshKey,
  className,
  siteGlue = false,
}: HireBuilderPreviewPanelProps) {
  const [iframeKey, setIframeKey] = React.useState(0);
  const [progress, setProgress] = React.useState(8);
  const displayAgentName = agentName.trim() || "your agent";
  const showGenerating =
    previewGenerationReady && (isGenerating || (!previewReady && websiteSubmitted));

  React.useEffect(() => {
    if (previewRefreshKey > 0) {
      setIframeKey((current) => current + 1);
    }
  }, [previewRefreshKey]);

  React.useEffect(() => {
    if (!showGenerating) {
      setProgress(8);
      return;
    }
    setProgress(12);
    const id = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 92) return p;
        const step = p < 40 ? 4 : p < 70 ? 2.5 : 1;
        return Math.min(92, p + step);
      });
    }, 400);
    return () => window.clearInterval(id);
  }, [showGenerating]);

  React.useEffect(() => {
    if (previewReady && previewProjectId) {
      setProgress(100);
    }
  }, [previewReady, previewProjectId]);

  const iframeSrc =
    previewProjectId && typeof window !== "undefined"
      ? `${window.location.origin}/agent/${previewProjectId}/Agent_Profile?embed=builder&refresh=${iframeKey}`
      : previewProjectId
        ? `/agent/${previewProjectId}/Agent_Profile?embed=builder&refresh=${iframeKey}`
        : null;

  const showPreview = Boolean(
    websiteSubmitted && previewProjectId && previewReady && iframeSrc,
  );

  const generatingLabel = siteGlue
    ? `Generating ${displayAgentName}'s AI Employee preview…`
    : "Generating your Inbound GPT Agent preview…";

  return (
    <div className={cn("relative flex min-h-0 flex-1 flex-col overflow-hidden bg-white", className)}>
      {showPreview ? (
        <iframe
          key={iframeKey}
          title="Agent profile preview"
          src={iframeSrc!}
          scrolling="yes"
          allow="microphone; camera; fullscreen; clipboard-write"
          className="absolute inset-0 h-full w-full border-0 bg-white"
        />
      ) : (
        <div className="flex min-h-[520px] flex-1 flex-col items-center justify-center gap-6 p-8 text-center">
          <div className="max-w-sm space-y-2">
            <p className="text-sm font-medium text-slate-900">
              {!websiteSubmitted
                ? "Your live agent preview will appear here."
                : !previewGenerationReady
                  ? "Almost there — finish the Build questions first."
                  : showGenerating
                    ? generatingLabel
                    : previewReady
                      ? "Your live agent preview will appear here."
                      : `Preparing ${displayAgentName}'s preview…`}
            </p>
            {!websiteSubmitted ? (
              <p className="text-sm text-slate-600">
                Share your website URL in the Build tab to get started — we&apos;ll analyze your
                site and generate a preview automatically.
              </p>
            ) : !previewGenerationReady ? (
              <p className="text-sm text-slate-600">
                Finish Trish&apos;s questions in the Build tab — your agent preview will generate
                automatically on this tab.
              </p>
            ) : showGenerating ? (
              <p className="text-sm text-slate-600">
                Building your workspace and live chat preview. This usually takes a few seconds.
              </p>
            ) : !isGenerating && !previewReady ? (
              <p className="text-sm text-slate-600">
                Preview updates automatically when you change settings in Build or Configure.
              </p>
            ) : null}
          </div>
          {showGenerating ? (
            <div className="w-full max-w-xs space-y-3">
              <div
                className="h-2 overflow-hidden rounded-full bg-slate-100"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
                aria-label="Preview generation progress"
              >
                <div
                  className="h-full rounded-full bg-[#4f81bd] transition-[width] duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs font-medium tabular-nums text-slate-500">
                {Math.round(progress)}%
              </p>
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
